import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CaretDown, CalendarCheck, ShieldCheck, Trophy, Hourglass, Clock, PencilSimpleLine, Megaphone, ArrowSquareOut, FileText, Info, Warning } from '@phosphor-icons/react';
import StatusTimeline from './StatusTimeline';
import { SearchReportInline } from './SearchReportCard';
import { OnboardingInline } from './OnboardingCard';
import { OfficeActionInline } from './OfficeActionCard';
import {
  chipFor, chipTextColor, resolveStageId, isRegistered, isClosed, needsAttention, isPaused, isPreparing, isPublication,
} from '../lib/stageModel';

const isLogoMark = (t) => t && !['STANDARD_CHARACTERS', 'STANDARD CHARACTER MARK', 'TYPED_DRAWING'].includes(t);

function fmt(d) {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}
const inFuture = (d) => { try { return d && new Date(d) >= new Date(new Date().toDateString()); } catch { return false; } };

// One nearest-future deadline per card + an ownership line. Empty → null. Past → null (suppressed).
function nearestDeadline(m) {
  const stage = resolveStageId(m);
  const candidates = [];
  if (inFuture(m.office_action_due_date) && !m.office_action_submitted) {
    candidates.push({
      date: m.office_action_due_date, label: 'Response due',
      owner: stage === '3779796689'
        ? 'Reply to our email so we can file your response.'
        : "We're drafting your response — nothing needed from you.",
    });
  }
  if (inFuture(m.sou_due_date)) {
    candidates.push({ date: m.sou_due_date, label: 'Statement of Use due', owner: "We'll prepare and file this with you." });
  }
  if (!candidates.length) return null;
  candidates.sort((a, b) => new Date(a.date) - new Date(b.date));
  return candidates[0];
}

function FiledWaitingPanel() {
  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid #ece5d8' }} data-testid="filed-waiting-panel">
      <div className="flex items-center gap-1.5 mb-2">
        <Hourglass weight="fill" className="w-4 h-4" style={{ color: '#1d57a5' }} />
        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#141b4d' }}>In line for examination</span>
      </div>
      <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
        Your application is in line for examination. The USPTO is currently taking about 4–5 months to issue a first
        response, and roughly 10 months on average from filing to a final outcome. We're watching your file daily —
        nothing is needed from you.
      </p>
    </div>
  );
}

// Post-search-approval, pre-filing: reassure the client we're drafting, and demote
// the search report to a compact link (the decision has already been made).
function PreparingPanel({ matter, onView }) {
  const stage = resolveStageId(matter);
  const isReady = stage === '3779796687';
  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid #ece5d8' }} data-testid={`preparing-panel-${matter.id}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <PencilSimpleLine weight="fill" className="w-4 h-4" style={{ color: '#1d57a5' }} />
        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#141b4d' }}>
          {isReady ? 'Ready to file' : 'Drafting your application'}
        </span>
      </div>
      <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
        {isReady
          ? "Your application is drafted and ready to file — we're submitting it to the USPTO shortly. We'll let you know the moment it's in. Nothing is needed from you."
          : "Your search cleared and we're drafting your application now. We'll let you know the moment it's filed with the USPTO. Nothing is needed from you."}
      </p>
      {matter.search_report_url && (
        <a href={matter.search_report_url} target="_blank" rel="noopener noreferrer"
          onClick={(e) => { e.stopPropagation(); onView?.(); }}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
          style={{ color: '#1d57a5' }} data-testid={`preparing-report-link-${matter.id}`}>
          <FileText weight="fill" className="w-4 h-4" /> View your search report
          <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}

// Quiet opposition window — nothing needed from the client.
function PublicationPanel({ matter }) {
  const cleared = resolveStageId(matter) === '3779796696';
  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid #ece5d8' }} data-testid={`publication-panel-${matter.id}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Megaphone weight="fill" className="w-4 h-4" style={{ color: '#1d57a5' }} />
        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#141b4d' }}>
          {cleared ? 'Opposition window cleared' : 'Published for opposition'}
        </span>
      </div>
      <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
        {cleared
          ? 'The opposition window closed with no objections. Your mark is moving toward registration — nothing is needed from you.'
          : "Your application published for opposition. This is a routine 30-day window where third parties can raise objections — most pass quietly. Nothing is needed from you; we're monitoring it daily."}
      </p>
    </div>
  );
}

// Explains a closed/abandoned matter — whether it was filed then closed, or never filed —
// so the client isn't left staring at a bare "Closed" chip with no context.
function ClosedPanel({ matter }) {
  const wasFiled = !!matter.serial_number;
  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid #ece5d8' }} data-testid={`closed-panel-${matter.id}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Info weight="fill" className="w-4 h-4" style={{ color: '#9a9488' }} />
        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#141b4d' }}>
          {wasFiled ? 'Application closed at the USPTO' : 'Closed before filing'}
        </span>
      </div>
      <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
        {wasFiled
          ? `This application${matter.serial_number ? ` (Serial ${matter.serial_number})` : ''} was filed but is now closed at the USPTO. That usually happens when a required response or deadline passed, or the application was withdrawn — it's not a reflection on your brand.`
          : 'This matter was closed before an application was filed with the USPTO — for example, if the search raised concerns or plans changed, so nothing was submitted.'}
      </p>
      <p className="text-sm leading-relaxed mt-3" style={{ color: '#52525B' }}>
        Want to understand what happened or explore {wasFiled ? 're-filing' : 'moving forward'}? Just reply to any email
        from us or book a call — we're happy to walk you through your options.
      </p>
    </div>
  );
}

function maintenanceAlert(m) {
  const dls = (m.uspto_deadlines || []).filter(
    (d) => d && d.stage && d.window_opens && d.actionable_date && ['overdue', 'urgent', 'soon'].includes(d.urgency)
  );
  if (!dls.length) return null;
  const order = { overdue: 0, urgent: 1, soon: 2 };
  dls.sort((a, b) => (order[a.urgency] - order[b.urgency]) || (a.actionable_date || '').localeCompare(b.actionable_date || ''));
  return dls[0];
}

function MaintenanceAlert({ d, hasProtectionPlan }) {
  const isPast = d.stage === 'overdue';
  const isGrace = d.stage === 'grace';
  const red = isPast || isGrace;
  const styles = red
    ? { bg: '#fef2f2', border: '#fca5a5', text: '#b91c1c' }
    : { bg: '#fffbeb', border: '#fcd34d', text: '#92400e' };
  let msg;
  if (isPast) {
    msg = <>The filing window for your <strong>{d.label}</strong> — including the late-filing grace period — closed on <strong>{fmt(d.grace_ends)}</strong>, so your registration may now be at risk of cancellation. {hasProtectionPlan
      ? 'Please contact us right away so we can help protect your registration.'
      : <>If you'd like our help sorting this out, ask us about the <Link to="/protection-plan" className="font-semibold underline" style={{ color: '#b91c1c' }}>Protection Plan</Link>.</>}</>;
  } else if (isGrace) {
    msg = <>The regular deadline for your <strong>{d.label}</strong> has passed. You can still file until <strong>{fmt(d.grace_ends)}</strong> by paying an additional late fee — after that your registration can be cancelled. {hasProtectionPlan
      ? "We're on it; reply to any email from us to get this filed."
      : <>Filing on time is your responsibility unless you're on our <Link to="/protection-plan" className="font-semibold underline" style={{ color: '#b91c1c' }}>Protection Plan</Link>.</>}</>;
  } else {
    msg = <>Your <strong>{d.label}</strong> is due by <strong>{fmt(d.deadline_closes)}</strong>. {d.description} {!hasProtectionPlan && <>Tracking and filing this for you is a <Link to="/protection-plan" className="font-semibold underline" style={{ color: '#92400e' }}>Protection Plan</Link> benefit.</>}</>;
  }
  return (
    <div className="mt-3 p-3 flex items-start gap-2" style={{ backgroundColor: styles.bg, border: `1px solid ${styles.border}` }} data-testid="maintenance-alert">
      <Warning weight="fill" className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: styles.text }} />
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide mb-0.5" style={{ color: styles.text }}>
          {isPast ? 'Deadline passed — action needed' : isGrace ? 'Deadline passed — late filing still possible' : 'Maintenance deadline coming up'}
        </p>
        <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>{msg}</p>
      </div>
    </div>
  );
}

function RegisteredPanel({ matter, hasProtectionPlan }) {
  const earliest = fmt(matter.earliest_date_8_can_be_filed);
  const latest = fmt(matter.latest_date_8_can_be_filed_without_paying_additional_fee);
  const regDate = fmt(matter.registration_date);
  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid #ece5d8' }} data-testid="registered-panel">
      <div className="flex items-center gap-1.5 mb-2">
        <Trophy weight="fill" className="w-4 h-4" style={{ color: '#006747' }} />
        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#141b4d' }}>Registered</span>
      </div>
      <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
        {regDate ? `Registered ${regDate}. ` : ''}Your mark is on the federal register.
        {earliest && latest ? ` To keep it, a maintenance filing is due between ${earliest} and ${latest}.` : ''}
      </p>
      <div className="mt-3 p-3 flex items-start gap-2" style={{ backgroundColor: '#f2e9db' }}>
        <ShieldCheck weight="duotone" className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#1d57a5' }} />
        <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
          {hasProtectionPlan
            ? "We're tracking your maintenance deadlines for you and will reach out well before anything is due."
            : <>We show these dates so you're aware of them. Please note that actively monitoring your renewals, sending reminders, and preparing and filing your maintenance documents goes beyond the trademark package you signed up for — that ongoing service is what our <Link to="/protection-plan" className="font-semibold hover:underline" style={{ color: '#1d57a5' }}>Protection Plan</Link> is for. Until then, keeping these deadlines is up to you.</>}
        </p>
      </div>
    </div>
  );
}

function SearchAwaitingPanel() {
  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid #ece5d8' }} data-testid="search-awaiting-panel">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h4 className="text-sm font-bold uppercase tracking-wide" style={{ color: '#1d57a5' }}>Search complete — report on its way</h4>
        <span className="text-xs font-bold px-2 py-1" style={{ backgroundColor: '#ffd100', color: '#141b4d' }}>In progress</span>
      </div>
      <div className="p-4 flex items-start gap-3" style={{ backgroundColor: '#ffffff', border: '1px solid #ece5d8', borderTop: '3px solid #141b4d' }}>
        <Hourglass weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#1d57a5' }} />
        <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
          We're completing your trademark search of the USPTO and other sources, and putting your
          plain-English report together. We'll post it right here — and email you — the moment it's
          ready. Nothing is needed from you yet.
        </p>
      </div>
    </div>
  );
}

function SearchReadyAwaitingPanel() {
  return (
    <div className="mt-4 pt-4" style={{ borderTop: '1px solid #ece5d8' }} data-testid="search-ready-awaiting-panel">
      <div className="p-4 flex items-start gap-3" style={{ backgroundColor: '#ffffff', border: '1px solid #ece5d8', borderTop: '3px solid #141b4d' }}>
        <Hourglass weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#1d57a5' }} />
        <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
          Your search report is ready and being posted here. In the meantime, check your email for
          the copy we sent you.
        </p>
      </div>
    </div>
  );
}

const OA_STAGES = ['4322435780', '4321107663', '4321107664', '3779796688', '3779796689'];
const SEARCH_NO_REPORT_STAGES = ['3779796682', '3779796683', '4321107658', '4321107659',
  '4321107660'];
// B5/B6 — search complete, report being posted here (distinct from B4 "on its way").
const SEARCH_READY_STAGES = ['4321107661', '4321107662'];

function contextLine(m) {
  const stage = resolveStageId(m);
  if (isClosed(m)) return m.serial_number
    ? 'This application was filed but is now closed at the USPTO — see what that means.'
    : 'This matter was closed before an application was filed — see the details.';
  if (['3779796689', '4321107663'].includes(stage)) return 'We need your go-ahead on an Office Action.';
  if (stage === '4321594099') return "The USPTO maintained its position after our response — we're reviewing your options and will be in touch about next steps.";
  if (stage === '4295976680') return "The USPTO has suspended your application — often while waiting on an earlier-filed mark. Nothing needed from you; we're monitoring it.";
  if (stage === '4321594101') return 'Your Notice of Allowance is in — we need your input on the next step.';
  if (stage === '4321594103') return 'Your Statement of Use is ready — we need your approval before we file it.';
  if (['4321594100', '4321594102'].includes(stage)) return "Your mark was allowed — we're handling the Statement of Use next steps.";
  if (OA_STAGES.includes(stage) || m.status_phase === 'office_action') return "The USPTO issued an Office Action — we're on it.";
  if (isPreparing(m)) return ['3779796687', '4322435778'].includes(stage)
    ? "Drafted and ready to file — we're submitting it to the USPTO shortly."
    : "We're drafting your application — we'll let you know the moment it's filed.";
  if (isPublication(m)) return 'Published for opposition — a routine window, nothing needed from you.';
  if (m.search_report_url) return 'Your search report is ready to review.';
  if (SEARCH_READY_STAGES.includes(stage))
    return 'Your search is complete — your report is being posted here. Meanwhile, check your email for the copy we sent you.';
  if (SEARCH_NO_REPORT_STAGES.includes(stage))
    return "Your trademark search is in progress — we're preparing your report and will share it here soon.";
  if (isRegistered(m)) return 'Registered — your mark is on the federal register.';
  if (m.serial_number) return 'Filed and in line for examination — nothing needed from you.';
  return "You're onboarded — a couple of quick steps, then we take it from here.";
}

export default function MatterCard({ matter, isFirstReport, defaultExpanded, hasProtectionPlan, onViewReport, onReportSeen, onDecision }) {
  const [expanded, setExpanded] = useState(!!defaultExpanded);

  // Stamp search_report_viewed_at the first time this report card renders.
  useEffect(() => {
    if (matter.search_report_url && !matter.search_report_viewed_at) {
      onReportSeen?.(matter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matter.id, matter.search_report_url, matter.search_report_viewed_at]);

  const chip = chipFor(matter);
  const registered = isRegistered(matter);
  const filedWaiting = !isPaused(matter) && !registered && matter.status_phase !== 'office_action'
    && !matter.search_report_url && (matter.serial_number || ['filed'].includes(matter.status_phase));
  const dl = nearestDeadline(matter);
  const mAlert = registered ? maintenanceAlert(matter) : null;

  const panel = () => {
    const stage = resolveStageId(matter);
    if (isClosed(matter)) return <ClosedPanel matter={matter} />;
    if (matter.journey_phase === 'PRE_SEARCH' || (!matter.hubspot_stage_id && matter.status_phase === 'onboarding'
        && !['SEARCH_READY', 'PREP_FILING'].includes(matter.journey_phase))) {
      return <OnboardingInline matter={matter} />;
    }
    if (OA_STAGES.includes(stage) || matter.status_phase === 'office_action') return <OfficeActionInline matter={matter} />;
    if (isPreparing(matter)) return <PreparingPanel matter={matter} onView={onViewReport} />;
    if (matter.search_report_url) return <SearchReportInline matter={matter} onView={onViewReport} isFirstReport={isFirstReport} onDecision={(d) => onDecision?.(matter, d)} />;
    if (SEARCH_READY_STAGES.includes(stage)) return <SearchReadyAwaitingPanel />;
    if (SEARCH_NO_REPORT_STAGES.includes(stage)) return <SearchAwaitingPanel />;
    if (isPublication(matter)) return <PublicationPanel matter={matter} />;
    if (registered) return <RegisteredPanel matter={matter} hasProtectionPlan={hasProtectionPlan} />;
    if (filedWaiting) return <FiledWaitingPanel />;
    return null;
  };

  return (
    <motion.div className="card-swiss" data-testid={`matter-card-${matter.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-2">
            <h3 className="text-xl font-semibold" style={{ color: '#141b4d' }}>{matter.trademark_name}</h3>
            {isLogoMark(matter.mark_drawing_type) && (
              <span className="text-xs font-medium px-2 py-0.5" style={{ backgroundColor: '#141b4d', color: '#ffd100' }}>LOGO</span>
            )}
            <span className="text-xs font-bold px-2.5 py-1 rounded-full" data-testid={`matter-chip-${matter.id}`}
              style={{ backgroundColor: chip.color, color: chipTextColor(chip.color) }}>{chip.label}</span>
          </div>
          <div className="flex flex-wrap gap-4 text-sm" style={{ color: '#9a9488' }}>
            {matter.serial_number && <span>Serial: <span className="font-medium" style={{ color: '#101820' }}>{matter.serial_number}</span></span>}
            {matter.registration_number && <span>Reg: <span className="font-medium" style={{ color: '#101820' }}>{matter.registration_number}</span></span>}
            {fmt(matter.filing_date) && <span>Filed: <span className="font-medium" style={{ color: '#101820' }}>{fmt(matter.filing_date)}</span></span>}
          </div>
        </div>
        <Link to={`/matters/${matter.id}`} className="btn-secondary flex items-center justify-center gap-2 whitespace-nowrap w-full lg:w-auto"
          data-testid={`view-matter-${matter.id}`}>View Details<ArrowRight weight="bold" className="w-4 h-4" /></Link>
      </div>

      <div className="mt-6"><StatusTimeline matter={matter} onViewReport={onViewReport} /></div>

      {/* Always-visible maintenance alert for registered marks in grace / overdue / due-soon */}
      {mAlert && <MaintenanceAlert d={mAlert} hasProtectionPlan={hasProtectionPlan} />}

      {/* One deadline line with ownership */}
      {dl && (
        <div className="mt-4 flex items-start gap-2 p-3" style={{ backgroundColor: '#f7f3ea' }} data-testid={`deadline-line-${matter.id}`}>
          <Clock weight="fill" className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#141b4d' }} />
          <p className="text-sm" style={{ color: '#101820' }}>
            <span className="font-semibold">{dl.label}: {fmt(dl.date)}</span> · {dl.owner}
          </p>
        </div>
      )}

      {/* Collapsed → one context line; Expanded → full stage panel */}
      {expanded ? (
        panel()
      ) : (
        <p className="mt-4 text-sm" style={{ color: '#52525B' }} data-testid={`context-line-${matter.id}`}>{contextLine(matter)}</p>
      )}

      {panel() && (
        <button onClick={() => setExpanded(!expanded)} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold hover:underline"
          style={{ color: '#1d57a5' }} data-testid={`toggle-details-${matter.id}`}>
          {expanded ? 'Hide details' : 'Show details'}
          <CaretDown weight="bold" className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      )}
    </motion.div>
  );
}

export { needsAttention };
